import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectIsQuizFeedbackOpen } from "../../app/slices/GlobalSlice";
import { selectTakes360 } from "../../app/slices/TrainingSlice";
import useShortcut, { ShortCuts } from "../../hooks/UseShortcutsHook";
import { TimelineClipboardContext } from "./CopyPasteContext";
import QuizFeedbackCollapsableColumn from "../quiz_feedback/QuizFeedbackCollapsableColumn";
import QuizFeedbackCollapsedRail from "../quiz_feedback/QuizFeedbackCollapsedRail";
import {
  QUIZ_FEEDBACK_FULL_COLUMN_WIDTH,
  QUIZ_FEEDBACK_RAIL_WIDTH,
  QUIZ_FEEDBACK_SPLIT_COLUMN_WIDTH,
  QUIZ_FEEDBACK_TRANSITION,
} from "../quiz_feedback/QuizFeedbackLayout";
import { EditorInteractionRowView } from "../editor_columns/EditorInteractionRow";
import Take360Column, { Take360ColumnRowView } from "./Take360Column";
import Take360InteractionColumn from "./Take360InteractionColumn";
import TakeInteractionDndProvider, {
  GroupDragOverlayBadge,
} from "./TakeInteractionDndProvider";
import "./TrainingTimeline360Content.css";
import { useTimelineCopyPaste } from "./useTimelineCopyPaste";

export default function TrainingTimeline360Content() {
  const {
    handleCopy,
    handleCut,
    handlePaste,
    handlePasteIntoSlot,
    handleDuplicate,
  } = useTimelineCopyPaste();
  const isQuizFeedbackOpen = useSelector(selectIsQuizFeedbackOpen);
  const takes = useSelector(selectTakes360);

  useShortcut(ShortCuts.Copy, handleCopy);
  useShortcut(ShortCuts.Cut, handleCut);
  useShortcut(ShortCuts.Paste, handlePaste);
  useShortcut(ShortCuts.Duplicate, handleDuplicate);

  const clipboardActions = useMemo(
    () => ({ handleCopy, handleCut, handlePaste, handlePasteIntoSlot }),
    [handleCopy, handleCut, handlePaste, handlePasteIntoSlot]
  );

  const takeColumnWidth = isQuizFeedbackOpen
    ? QUIZ_FEEDBACK_RAIL_WIDTH
    : QUIZ_FEEDBACK_FULL_COLUMN_WIDTH;
  const interactionColumnWidth = isQuizFeedbackOpen
    ? QUIZ_FEEDBACK_SPLIT_COLUMN_WIDTH
    : QUIZ_FEEDBACK_FULL_COLUMN_WIDTH;

  return (
    <TimelineClipboardContext.Provider value={clipboardActions}>
    <TakeInteractionDndProvider
      takes={takes}
      renderOverlay={(activeDrag) => {
        if (!activeDrag) return null;
        if (activeDrag.kind === "take") {
          return (
            <Take360ColumnRowView
              take={activeDrag.take}
              selected={false}
              ghost
            />
          );
        }
        return (
          <GroupDragOverlayBadge count={activeDrag.interactions.length}>
            <EditorInteractionRowView
              interaction={activeDrag.interaction}
              ghost
              testIdPrefix="take360-interaction-row"
            />
          </GroupDragOverlayBadge>
        );
      }}
    >
      <div
        className="trainingtimeline360content-root"
        data-testid="trainingtimeline-360-columns"
      >
      {/* the takes column collapses to a slim rail while quiz feedback is open */}
      <div
        className="h-full overflow-hidden"
        style={{
          width: takeColumnWidth,
          minWidth: takeColumnWidth,
          transition: QUIZ_FEEDBACK_TRANSITION,
        }}
      >
        {isQuizFeedbackOpen ? <QuizFeedbackCollapsedRail /> : <Take360Column />}
      </div>
      <div
        className="h-full overflow-hidden"
        style={{
          width: interactionColumnWidth,
          minWidth: interactionColumnWidth,
          transition: QUIZ_FEEDBACK_TRANSITION,
        }}
      >
        <Take360InteractionColumn />
      </div>
        <QuizFeedbackCollapsableColumn />
      </div>
    </TakeInteractionDndProvider>
    </TimelineClipboardContext.Provider>
  );
}
